import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

function ComplexStatisticsCard({
                                 color,
                                 title,
                                 count,
                                 percentage,       // { amount: "+12.3%", color: "success", label: "vs ..." }
                                 previous,         // <-- NEW: { amount: "987,654 ₪", label: "Previous month" }
                                 icon,
                                 dailyComparison,
                                 avgComparison,
                               }) {
  return (
      <Card
          sx={{
            height: "220px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
      >
        <MDBox display="flex" justifyContent="space-between" pt={1} px={2}>
          <MDBox
              variant="gradient"
              bgColor={color}
              color={color === "light" ? "dark" : "white"}
              coloredShadow={color}
              borderRadius="xl"
              display="flex"
              justifyContent="center"
              alignItems="center"
              width="4rem"
              height="4rem"
              mt={0}
          >
            <Icon fontSize="medium" color="inherit">
              {icon}
            </Icon>
          </MDBox>

          {/* Title + current count + MoM % (right-aligned) */}
          <MDBox textAlign="right" lineHeight={1.25}>
            <MDTypography variant="button" fontWeight="light" color="text">
              {title}
            </MDTypography>

            {/* Count with the MoM % right next to it */}
            <MDBox display="flex" alignItems="baseline" justifyContent="flex-end" gap={1}>
              <MDTypography variant="h4">{count}</MDTypography>
              {percentage?.amount && (
                  <MDTypography
                      variant="button"
                      fontWeight="bold"
                      color={percentage.color}
                      sx={{ whiteSpace: "nowrap" }}
                  >
                    {percentage.amount}
                  </MDTypography>
              )}
            </MDBox>
          </MDBox>
        </MDBox>

        {avgComparison && (
            <MDTypography sx={{ marginLeft: "5px" }} component="p" variant="button" color="text" display="flex">
              <MDTypography component="span" variant="button" fontWeight="bold" color={avgComparison.color}>
                {avgComparison.amount}
              </MDTypography>
              &nbsp;{avgComparison.label}
            </MDTypography>
        )}

        {dailyComparison && (
            <MDTypography sx={{ marginLeft: "5px" }} component="p" variant="button" color="text" display="flex">
              <MDTypography component="span" variant="button" fontWeight="bold" color={dailyComparison.color}>
                {dailyComparison.amount}
              </MDTypography>
              &nbsp;{dailyComparison.label}
            </MDTypography>
        )}

        <Divider />

        {/* Bottom line now shows the PREVIOUS month amount + the label/date range */}
        <MDBox pb={2} px={2} display="flex" flexDirection="column" gap={0.5}>
          {previous?.amount && (
              <MDTypography component="p" variant="button" color="text" display="flex">
                <MDTypography component="span" variant="button" fontWeight="bold">
                  {previous.amount}
                </MDTypography>
                &nbsp;{percentage?.label || previous.label}
              </MDTypography>
          )}
        </MDBox>
      </Card>
  );
}

// Defaults
ComplexStatisticsCard.defaultProps = {
  color: "info",
  percentage: {
    color: "success",
    text: "",
    label: "",
  },
  previous: null,
};

// PropTypes
ComplexStatisticsCard.propTypes = {
  color: PropTypes.oneOf([
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "light",
    "dark",
  ]),
  title: PropTypes.string.isRequired,
  count: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  percentage: PropTypes.shape({
    color: PropTypes.oneOf([
      "primary",
      "secondary",
      "info",
      "success",
      "warning",
      "error",
      "dark",
      "white",
    ]),
    amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    label: PropTypes.string,
  }),
  previous: PropTypes.shape({
    amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    label: PropTypes.string,
  }), // <-- NEW
  icon: PropTypes.node.isRequired,
  dailyComparison: PropTypes.object,
  avgComparison: PropTypes.object,
};

export default ComplexStatisticsCard;
